import React from 'react';
import type { GamificationLevel } from '@troia-v3/types';

import { Input } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { SettingsSection } from './SettingsSection';

interface Props { levels: GamificationLevel[]; onChange: (levels: GamificationLevel[]) => void; disabled?: boolean }

/** Sigla dos 4 níveis padrão (mockup 10-A: BRZ · PRT · OUR · DIA) — comparação sem acento/caixa. */
const STANDARD_LEVEL_TAGS: Record<string, string> = { bronze: 'BRZ', prata: 'PRT', ouro: 'OUR', diamante: 'DIA' };
const normalizeLevelName = (name: string): string => name.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
const tag = (name: string): string => STANDARD_LEVEL_TAGS[normalizeLevelName(name)]
  ?? (name.replace(/[^A-Za-zÀ-ú]/g, '').slice(0, 3).toUpperCase() || '—');

/** Validação espelho do Zod (`levelSchema`/`superRefine` em `gamification/validation.ts`):
 * nome 1–40 chars, `levels` crescentes, primeiro em 0. Evita 422 sem explicação ao salvar. */
export function levelsError(levels: GamificationLevel[]): string | null {
  if (levels.length === 0) return 'Cadastre pelo menos um nível';
  if (levels[0]?.minPoints !== 0) return 'O primeiro nível precisa começar em 0 pontos';
  for (let i = 0; i < levels.length; i += 1) {
    const cur = levels[i];
    if (!cur?.name.trim()) return 'Todo nível precisa de nome';
    if (cur.name.trim().length > 40) return 'Nome do nível deve ter no máximo 40 caracteres';
    const prev = levels[i - 1];
    if (prev && cur.minPoints <= prev.minPoints) return 'Os pontos mínimos precisam ser crescentes e sem repetição';
  }
  return null;
}

export const LevelsEditor: React.FC<Props> = ({ levels, onChange, disabled = false }) => {
  const set = (i: number, p: Partial<GamificationLevel>) => onChange(levels.map((l, idx) => (idx === i ? { ...l, ...p } : l)));
  const remove = (i: number) => onChange(levels.filter((_, idx) => idx !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= levels.length) return;
    const next = [...levels];
    const a = next[i]; const b = next[j];
    if (!a || !b) return;
    next[i] = b; next[j] = a;
    onChange(next);
  };
  const add = () => {
    const last = levels[levels.length - 1];
    onChange([...levels, { name: '', minPoints: (last?.minPoints ?? 0) + 1000 }]);
  };
  const error = levelsError(levels);

  return (
    <SettingsSection icon="award" title="Níveis" description="Faixas por pontos acumulados na carreira — nunca zeram. Aparecem em Minhas Vendas." testId="gamification-section-levels">
      {levels.map((level, i) => (
        <div key={i} className="gm-lvl-row" data-testid={`gamification-level-row-${i}`}>
          <span className={`gm-lvl-tag gm-lvl-tag--${i < 4 ? i : 'n'}`}>{tag(level.name)}</span>
          <Input value={level.name} onChange={(e) => set(i, { name: e.target.value })} placeholder="Nome do nível" disabled={disabled} data-testid={`gamification-level-name-${i}`} />
          <Input
            className="gm-num"
            style={{ width: '100%' }}
            inputMode="numeric"
            value={String(level.minPoints)}
            disabled={disabled || i === 0}
            onChange={(e) => set(i, { minPoints: Math.max(0, Math.floor(Number(e.target.value.replace(/\D/g, '')) || 0)) })}
            data-testid={`gamification-level-min-${i}`}
          />
          <button type="button" className="h-8 w-8 rounded-lg inline-flex items-center justify-center disabled:opacity-30" style={{ color: 'var(--theme-muted-text)' }} onClick={() => move(i, 1)} disabled={disabled || i === 0 || i === levels.length - 1} aria-label="Mover para baixo" data-testid={`gamification-level-down-${i}`}><Icon name="drag" size={16} /></button>
          <button type="button" className="h-8 w-8 rounded-lg inline-flex items-center justify-center disabled:opacity-30" style={{ color: 'var(--theme-muted-text)' }} onClick={() => remove(i)} disabled={disabled || i === 0} aria-label="Remover nível" data-testid={`gamification-level-remove-${i}`}><Icon name="x" size={16} /></button>
        </div>
      ))}
      <div className="text-xs mt-1.5" style={{ color: 'var(--theme-muted-text)' }}>Nome · pontos mínimos</div>
      {error && <div className="text-xs mt-1.5" style={{ color: 'var(--theme-error-fg)' }} data-testid="gamification-levels-error">{error}</div>}
      <button type="button" className="gm-add-row" onClick={add} disabled={disabled || levels.length >= 10} data-testid="gamification-level-add"><Icon name="plus" size={14} />Adicionar nível</button>
    </SettingsSection>
  );
};
LevelsEditor.displayName = 'LevelsEditor';
